import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/* "What should I look at next?" — one table, mixing every kind of thing that can want a person.
 *
 * The design spec adds this as a shared component with four columns: What / Why it needs you / Since
 * / an action link. The mixing is the point rather than a convenience: a steward's morning question
 * is not "are there incidents" and then "are there reviews", it is "what is worst". A dashboard that
 * answers with four separate lists makes the reader do the merging, and the thing they most need to
 * see is whichever row is at the top of the merged order.
 *
 * **`why` is a sentence, not a category.** "Verification failed" is a state; "the source refused the
 * credential" is why it needs a person. The platform already returns the second — RFC 9457 `detail`
 * — and a table that renders the first is throwing away the useful half.
 *
 * **`since` is required and is never a bare timestamp.** Age is what turns a list into a priority:
 * an unreviewed classification from three days ago and one from three minutes ago are different
 * problems. Callers pass an already-humanised string so this component holds no clock and no locale
 * logic of its own.
 */

export interface AttentionRow {
  /** Stable across re-renders and unique within the table — the React key. */
  readonly id: string;
  /** The subject: a dataset, a rule, a connection. Rendered as-is so a caller can pass mono type. */
  readonly what: ReactNode;
  /** Why a person is needed, in a sentence. */
  readonly why: string;
  /** How long it has been waiting, already humanised. */
  readonly since: string;
  /** Where to go to act on it. */
  readonly to: string;
  /** The link's words. Names the destination, never "click here". */
  readonly action: string;
  /** Highest first. The caller owns the ordering; this component does not re-sort. */
  readonly tone?: 'danger' | 'warn' | 'neutral';
}

export interface AttentionTableProps {
  readonly rows: readonly AttentionRow[];
  /** Shown when `rows` is empty *and the reads succeeded* — never when one failed. A caller that
   * cannot tell those apart must not render this component: "nothing needs you" and "we could not
   * find out" are different facts, and this is the surface where confusing them matters most. */
  readonly empty: string;
}

export function AttentionTable({ rows, empty }: AttentionTableProps): ReactElement {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return <p className="lq-attention__empty">{empty}</p>;
  }

  return (
    <div className="lq-table-scroll">
      <table className="lq-table lq-attention">
        <thead>
          <tr>
            <th scope="col">{t('attention.what')}</th>
            <th scope="col">{t('attention.why')}</th>
            <th scope="col">{t('attention.since')}</th>
            {/* An empty header for the action column, labelled for assistive technology rather than
             * left nameless: a screen reader announcing "column 4" tells a user nothing. */}
            <th scope="col">
              <span className="lq-visually-hidden">{t('attention.action')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {/* The column is classed so it can be *bounded* — see the stylesheet. Without a class
                  on this cell there is nothing for the `why` column's slack absorption to work
                  against, and an unnamed subject's explanatory sentence takes the width the sentence
                  that matters was supposed to have. */}
              <td className="lq-attention__what">
                <span
                  className={
                    row.tone === undefined
                      ? 'lq-attention__mark'
                      : `lq-attention__mark lq-attention__mark--${row.tone}`
                  }
                  aria-hidden="true"
                />
                {row.what}
              </td>
              <td className="lq-attention__why">{row.why}</td>
              <td className="lq-attention__since">{row.since}</td>
              {/* Both classes: `lq-table__actions` is the shared right-hand alignment every table
                  uses, and `lq-attention__act` is what carries this table's own link treatment — see
                  the stylesheet for why the underline is dropped at rest *here* and nowhere else. */}
              <td className="lq-table__actions lq-attention__act">
                <Link to={row.to}>{row.action}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
